import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/api";
import { useAuth } from "@/auth";
import { Card, Btn, Spinner } from "@/components/ui-kit";
import { EmptyState } from "@/components/dashboard-bits";
import { ProBadge } from "@/components/dashboard-bits";
import { availMeta } from "@/components/profile-helpers";
import { MapPin, MessageSquare, Search, SlidersHorizontal, ChevronLeft, ChevronRight, Sparkles, Lock, Check } from "lucide-react";
export { availMeta, videoEmbedUrl } from "@/components/profile-helpers";
import { CATEGORY_OPTIONS, SERVICE_OPTIONS } from "@/constants/taxonomy";

const selectCls = "rounded-xl bg-white border border-[#E6E1D9] px-3.5 py-2.5 text-sm outline-none focus:border-[#292827] transition-colors appearance-none";

const PRO_BENEFITS = [
  "Julkaise rajattomasti kampanjoita",
  "Selaa sisällöntuottajahakemistoa",
  "Ota suoraan yhteyttä Creatori Pro -jäseniin",
];

export function DirectoryLocked() {
  const navigate = useNavigate();
  return (
    <div data-testid="directory-locked" className="max-w-lg">
      <Card className="p-10 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-[#E9EAEC] flex items-center justify-center text-[#292827]"><Lock size={22} strokeWidth={1.75} /></div>
        <h3 className="mt-6 text-2xl font-semibold tracking-display text-[#292827]">Sisällöntuottajahakemisto kuuluu Business Pro -jäsenyyteen</h3>
        <p className="mt-3 text-[15px] text-[#6E675C] leading-relaxed">Business Pro -jäsenyydellä voit etsiä sopivia sisällöntuottajia ja ottaa suoraan yhteyttä Creatori Pro -jäseniin.</p>
        <div className="mt-7 space-y-3 text-left max-w-xs mx-auto">
          {PRO_BENEFITS.map((b) => (
            <div key={b} className="flex items-center gap-3 text-sm text-[#292827]">
              <span className="h-6 w-6 rounded-full bg-[#E9EAEC] flex items-center justify-center shrink-0"><Check size={14} strokeWidth={2} /></span>
              {b}
            </div>
          ))}
        </div>
        <Btn className="mt-8 w-full" disabled data-testid="upgrade-business-pro">
          Tulossa pian
        </Btn>
        <p className="mt-3 text-xs text-[#968F85]">Pro-jäsenyys julkaistaan myöhemmin.</p>
      </Card>
    </div>
  );
}


export const FREE_CONTACT_MSG = "Suora yhteydenotto on käytettävissä vain Creatori Pro -jäsenille. Voit ottaa yhteyttä tähän sisällöntuottajaan hänen hakiessaan kampanjaasi.";

function DiscoveryCard({ c, onView, onContact, wide }) {
  const p = c.profile || {};
  const photo = p.profile_photo || c.picture;
  const initials = (c.name || "?").slice(0, 2).toUpperCase();
  const thumbs = (p.portfolio_images && p.portfolio_images.length ? p.portfolio_images : p.thumbnails) || [];
  return (
    <Card className={`group overflow-hidden flex flex-col rounded-3xl !border-transparent transition-all duration-300 hover:soft-shadow-hover ${wide ? "" : "w-[300px] shrink-0"}`} data-testid="creator-card">
      <div className="relative h-64 w-full overflow-hidden bg-[#E9EAEC] cursor-pointer rounded-3xl" onClick={onView}>
        {photo ? <img src={fileUrl(photo)} alt={c.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          : <div className="h-full w-full flex items-center justify-center text-2xl font-medium text-[#968F85]">{initials}</div>}
        {p.availability && (() => { const a = availMeta(p.availability); return (
          <span className={`absolute top-4 left-4 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${a.muted ? "text-[#968F85]" : "text-[#292827]"}`} data-testid="card-availability">
            <span className={`h-1.5 w-1.5 rounded-full ${a.dot}`} /> {a.label}
          </span>
        ); })()}
      </div>
      <div className="px-1 pt-5 flex flex-col flex-1">
        <div className="flex items-center gap-2">
          <h4 className="text-[17px] font-semibold tracking-tightish text-[#292827] truncate">{c.name}</h4>
          {p.is_pro && <ProBadge />}
        </div>
        {p.location && (
          <p className="text-xs text-[#968F85] flex items-center gap-1 mt-1.5 truncate"><MapPin size={12} className="text-[#968F85]" /> {p.location}</p>
        )}
        {p.bio && <p className="mt-3.5 text-[15px] text-[#3A3733] leading-relaxed line-clamp-3" data-testid="creator-card-bio">{p.bio}</p>}
        {p.content_types?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="creator-card-services">
            {p.content_types.slice(0, 3).map((s) => (
              <span key={s} className="rounded-full bg-[#F1F0ED] px-2.5 py-1 text-[11px] font-medium text-[#3A3733]">{s}</span>
            ))}
            {p.content_types.length > 3 && (
              <span className="rounded-full bg-[#E9EAEC] px-2.5 py-1 text-[11px] font-medium text-[#6E675C]" data-testid="creator-card-services-more">+{p.content_types.length - 3}</span>
            )}
          </div>
        )}
        {thumbs.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2">
            {thumbs.slice(0, 3).map((t, i) => <div key={i} className="aspect-square rounded-xl overflow-hidden bg-[#E9EAEC]"><img src={fileUrl(t)} alt="" className="h-full w-full object-cover" /></div>)}
          </div>
        )}
        <div className="mt-6 pt-1 flex-1 flex flex-col justify-end">
          <span className="text-[11px] text-[#968F85]" data-testid="creator-card-price">{p.price_range || "Hinta neuvoteltavissa"}</span>
          <div className="mt-3 flex gap-2">
            <Btn variant="primary" className="flex-1 !px-3 !py-3 !text-sm !rounded-xl" onClick={onView} data-testid="view-profile">Näytä profiili</Btn>
            {p.is_pro && (
              <Btn variant="outline" className="!px-3 !py-3 !text-sm !rounded-xl" onClick={onContact} data-testid="contact-creator"><MessageSquare size={15} /></Btn>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function Row({ title, icon: Icon, children }) {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 560, behavior: "smooth" });
  return (
    <section className="mb-12">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold tracking-tightish text-[#292827] flex items-center gap-2">{Icon && <Icon size={18} />} {title}</h3>
        <div className="hidden md:flex gap-2">
          <button onClick={() => scroll(-1)} className="h-9 w-9 rounded-full border border-[#E6E1D9] flex items-center justify-center hover:border-[#292827] transition"><ChevronLeft size={17} /></button>
          <button onClick={() => scroll(1)} className="h-9 w-9 rounded-full border border-[#E6E1D9] flex items-center justify-center hover:border-[#292827] transition"><ChevronRight size={17} /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x scroll-smooth" style={{ scrollbarWidth: "none" }}>
        {children}
      </div>
    </section>
  );
}

const DEFAULT_FILTERS = { q: "", location: "all", category: "all", content_type: "all", price: "all", availability: "all", sort: "newest" };

export default function CreatorsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const locked = user?.role === "business" && !user?.profile?.is_pro;
  const [discovery, setDiscovery] = useState(null);
  const [results, setResults] = useState(null);
  const [options, setOptions] = useState({ locations: [], categories: [], content_types: [], prices: [], availability: [] });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  const active = filters.q.trim() !== "" || ["location", "category", "content_type", "price", "availability"].some((k) => filters[k] !== "all");

  useEffect(() => {
    if (locked) return;
    api.get("/creators/filters/options").then((r) => setOptions(r.data));
    api.get("/creators/discovery").then((r) => setDiscovery(r.data));
  }, [locked]);

  const loadResults = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    ["location", "category", "content_type", "price", "availability"].forEach((k) => { if (filters[k] !== "all") params.set(k, filters[k]); });
    params.set("sort", filters.sort);
    const { data } = await api.get(`/creators?${params.toString()}`);
    setResults(data);
  }, [filters]);

  useEffect(() => {
    if (!active) { setResults(null); return; }
    const t = setTimeout(loadResults, 250);
    return () => clearTimeout(t);
  }, [active, loadResults]);

  const openProfile = (id) => navigate(`/dashboard/sisallontuottajat/${id}`);
  const contact = (id) => navigate(`/dashboard/viestit?user=${id}`);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  if (locked) return <DirectoryLocked />;
  if (!discovery) return <Spinner />;

  return (
    <div data-testid="creators-page">
      {/* Hero */}
      <div className="mb-8">
        <h3 className="text-3xl md:text-4xl font-semibold tracking-display text-[#292827]">Löydä lahjakkuudet</h3>
        <p className="text-[15px] text-[#6E675C] mt-2 max-w-xl">Tutustu Suomen sisällöntuottajiin ja löydä täydellinen tekijä seuraavaan kampanjaasi.</p>
      </div>

      {/* Search + filters */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md -mx-5 md:-mx-10 px-5 md:px-10 py-3 mb-8 border-b border-[#E6E1D9]">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#968F85]" />
            <input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Hae sisällöntuottajia nimellä, biolla tai sijainnilla..."
              data-testid="creator-search"
              className="w-full rounded-full bg-white border border-[#E6E1D9] pl-11 pr-4 py-3 text-[15px] outline-none focus:border-[#292827] transition-colors" />
          </div>
          <button onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters"
            className="shrink-0 inline-flex items-center gap-2 rounded-full border border-[#E6E1D9] px-4 py-3 text-sm font-medium hover:border-[#292827] transition">
            <SlidersHorizontal size={16} /> Suodattimet
          </button>
        </div>
        {showFilters && (
          <div className="mt-3 flex flex-wrap gap-2">
            <select className={selectCls} value={filters.location} onChange={(e) => set("location", e.target.value)} data-testid="filter-location">
              <option value="all">Kaikki sijainnit</option>
              {options.locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select className={selectCls} value={filters.category} onChange={(e) => set("category", e.target.value)} data-testid="filter-creator-category">
              <option value="all">Kaikki kategoriat</option>
              {CATEGORY_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select className={selectCls} value={filters.content_type} onChange={(e) => set("content_type", e.target.value)} data-testid="filter-creator-content-type">
              <option value="all">Kaikki palvelut</option>
              {SERVICE_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select className={selectCls} value={filters.price} onChange={(e) => set("price", e.target.value)} data-testid="filter-price">
              <option value="all">Kaikki hinnat</option>
              {options.prices.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select className={selectCls} value={filters.availability} onChange={(e) => set("availability", e.target.value)} data-testid="filter-availability">
              <option value="all">Kaikki saatavuudet</option>
              {options.availability.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            {active && <button onClick={() => setFilters(DEFAULT_FILTERS)} className="text-sm text-[#6E675C] hover:text-[#292827] px-3" data-testid="clear-filters">Tyhjennä</button>}
          </div>
        )}
      </div>

      {active ? (
        /* Results grid */
        !results ? <Spinner /> : results.length === 0 ? (
          <EmptyState title="Ei sisällöntuottajia hakuehdoilla" subtitle="Kokeile muuttaa hakua tai suodattimia." />
        ) : (
          <>
            <p className="text-sm text-[#6E675C] mb-4">{results.length} sisällöntuottajaa</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {results.map((c) => <DiscoveryCard key={c.user_id} c={c} wide onView={() => openProfile(c.user_id)} onContact={() => contact(c.user_id)} />)}
            </div>
          </>
        )
      ) : (
        /* Discovery rows */
        <>
          {discovery.recommended.length > 0 && (
            <Row title="Suositellut sisällöntuottajat" icon={Sparkles}>
              {discovery.recommended.map((c) => <div key={c.user_id} className="snap-start"><DiscoveryCard c={c} onView={() => openProfile(c.user_id)} onContact={() => contact(c.user_id)} /></div>)}
            </Row>
          )}
          <Row title="Uusimmat sisällöntuottajat">
            {discovery.newest.map((c) => <div key={c.user_id} className="snap-start"><DiscoveryCard c={c} onView={() => openProfile(c.user_id)} onContact={() => contact(c.user_id)} /></div>)}
          </Row>
          {discovery.rows.map((row) => (
            <Row key={row.key} title={row.label}>
              {row.creators.map((c) => <div key={c.user_id} className="snap-start"><DiscoveryCard c={c} onView={() => openProfile(c.user_id)} onContact={() => contact(c.user_id)} /></div>)}
            </Row>
          ))}
        </>
      )}
    </div>
  );
}
